import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { HubDialogContent, HubHookFormWrapper } from "../../components";
import {
  useGetShipmentPackagesDetails,
  usePostShipment,
  useUpdateShipmentPackages,
} from "../../hooks";
import { usePutAdminCloseWorkOrder } from "../../hooks/react-query/useWorkOrderData";
import { getUserLocation } from "../../redux";
import { getVendorStatus } from "../../redux/shipping";

import { WorkOrderSerializationDialog } from "../WorkOrderSerializationDialog";
import { useDialogContext } from "../HubDialogWrapper";

import ShipmentDetailsDialogForm from "./ShipmentDetailsDialogForm";
import ShipmentDetailsDialogTitle from "./ShipmentDetailsDialogTitle";
import { boxPayloadMapper, getBoxFormDefaults } from "./helper";
import { ShipmentDetailsSkeleton } from "./Skeleton";
import CloseOpenWorkOrdersDialog from "./CloseOpenWorkOrdersDialog";

const workOrderNumberOf = (wo) => wo?.workOrder || wo?.label;

const isOpenWorkOrder = (wo) => {
  if (!wo) return false;
  const done = (wo.completed ?? 0) + (wo.scrapped ?? 0);
  return done < (wo.quantity ?? 0);
};

const ShipmentDetailsDialogContent = ({ defaultLocation = 1, isReturning }) => {
  const { onClose = () => {}, open: id } = useDialogContext();

  const { data: shipment, isLoading: isGetLoading } =
    useGetShipmentPackagesDetails(id);

  const [searchParams] = useSearchParams();
  const locationId = searchParams.get("location") || defaultLocation;

  const { mutate: postShipmentData, isSuccess: isPostSuccess } =
    usePostShipment();
  const { mutate: putShipmentData, isSuccess: isPutSuccess } =
    useUpdateShipmentPackages();
  const { mutateAsync: closeWorkOrder, isLoading: isClosing } =
    usePutAdminCloseWorkOrder();

  // Snapshot of what was sent + the WO option set at submit time. Used after
  // the post succeeds to figure out which work orders are still open and
  // worth prompting to close.
  const submittedRef = useRef(null);
  const optionsRef = useRef([]);
  const [openCloses, setOpenCloses] = useState(null); // [{ workOrder, ... }]

  const isLoading = isGetLoading;

  useEffect(() => {
    if (!(isPostSuccess || isPutSuccess)) return;

    // Edits never trigger the close prompt — only the initial create.
    if (isPutSuccess) {
      onClose();
      return;
    }

    const submitted = submittedRef.current;
    if (!submitted) {
      onClose();
      return;
    }
    const { customerId, returning, items } = submitted;
    if (returning || !customerId || !items?.length) {
      onClose();
      return;
    }

    const byId = new Map(optionsRef.current.map((o) => [o.id, o]));
    const open = items
      .map((it) => byId.get(it.id))
      .filter(isOpenWorkOrder);

    if (!open.length) {
      onClose();
      return;
    }

    setOpenCloses(open);
  }, [isPostSuccess, isPutSuccess]);

  const onSubmit = (formData) => {
    const mapData = {
      id: shipment?.id,
      formData,
      locationId: shipment?.locationId || defaultLocation,
    };
    const payload = boxPayloadMapper(mapData);

    submittedRef.current = {
      customerId: payload.customerId,
      returning: payload.returning,
      items: payload.items,
    };

    const request = shipment?.id ? putShipmentData : postShipmentData;
    request(payload);
  };

  const handleConfirmCloses = async () => {
    if (!openCloses?.length) return;
    // Run sequentially so a mid-batch failure leaves a clear point to retry.
    for (const wo of openCloses) {
      const workOrder = workOrderNumberOf(wo);
      if (!workOrder) continue;
      // eslint-disable-next-line no-await-in-loop
      await closeWorkOrder({ id: workOrder });
    }
    setOpenCloses(null);
    onClose();
  };

  const handleSkipCloses = () => {
    setOpenCloses(null);
    onClose();
  };

  const defaultValues = getBoxFormDefaults(shipment, locationId, isReturning);
  return (
    <>
      <HubDialogContent
        sx={{ height: "calc(100vh - 50px)", overflow: "hidden" }}
        disablePadding
      >
        {isLoading ? (
          <ShipmentDetailsSkeleton />
        ) : (
          <HubHookFormWrapper
            onSubmit={onSubmit}
            defaultValues={defaultValues}
            sx={{ height: "calc(100vh - 50px)", overflow: "hidden" }}
            useReset={false}
          >
            <ShipmentDetailsDialogTitle
              number={shipment?.id}
              onClose={onClose}
            />
            <ShipmentDetailsDialogForm
              shipment={shipment}
              locationId={locationId}
              onOptionsChange={(opts) => {
                optionsRef.current = opts || [];
              }}
            />
            <WorkOrderSerializationDialog />
          </HubHookFormWrapper>
        )}
      </HubDialogContent>

      <CloseOpenWorkOrdersDialog
        open={!!openCloses}
        workOrders={openCloses || []}
        onConfirm={handleConfirmCloses}
        onClose={handleSkipCloses}
        isClosing={isClosing}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  defaultLocation: getUserLocation(state),
  isReturning: getVendorStatus(state),
});
export default connect(mapStateToProps)(ShipmentDetailsDialogContent);
